import type { LeadSource } from '../interfaces/ILogistics.js';
import type { ISnapshotBody } from '../apis/logistics.js';
import { sameUnit, unitToStore } from '../../logistics/index.js';
import { fold, parseNumber } from '../utils/text.js';
import { amountIssue, type QuantityIssue } from './quantity.js';
import type { IStockPrefill } from './stockPrefill.js';

/** The fields the dialog seeds from its props, as the strings they render as.
 *  `undefined` for anything not supplied — only a value we actually put there
 *  earns the amber treatment. */
/**
 * The lead the field opens with: this material's own, else the site average.
 *
 * The fallback is what makes the field answerable on a blank "Add stock" — no
 * material means no level means no lead, and the alternative was an empty box
 * demanding a guess on a site that already knows roughly how long its
 * deliveries take.
 */
export function openingLead(
  prefill: IStockPrefill | null | undefined,
  siteMedianLeadDays: number | null | undefined,
  leadsByMaterial: Record<string, { days: number; source: LeadSource }> = {},
  materialName = '',
): { days: number | null; source: LeadSource | 'none' } {
  // A sync offers the measured lead from the arrival being reconciled — it must
  // take the field over the row's committed `leadTimeDays`, which is still the
  // pre-sync value (the row only moves once the arrival is synced). Never
  // 'estimated', so `seedMap` marks it amber like the other filled-in fields.
  if (prefill?.suggestedLeadTimeDays != null) {
    return { days: prefill.suggestedLeadTimeDays, source: prefill.suggestedLeadSource ?? 'site-median' };
  }
  if (prefill?.leadTimeDays != null) {
    return { days: prefill.leadTimeDays, source: prefill.leadSource ?? 'site-median' };
  }
  // A blank "Add stock" has no prefill, but once a tracked material is picked
  // it has a lead of its own — and showing the site average instead would be
  // the wrong number, not merely a vague one.
  // Folded, like every other material lookup in this file (`deliveredAmounts`,
  // the unit and suggestion matching). Keyed raw, typing `鉄筋 d16` missed a
  // material tracked as `鉄筋 D16`: the amount prefilled from its delivery while
  // the lead fell back to the site average, under a caption asserting the
  // material had no delivery history.
  const picked = materialName.trim() === '' ? undefined : leadsByMaterial[fold(materialName)];
  if (picked) return picked;
  if (siteMedianLeadDays != null) return { days: siteMedianLeadDays, source: 'site-median' };
  return { days: null, source: 'none' };
}

export function seedMap(
  prefill: IStockPrefill | null,
  estimatedRemaining: number | undefined,
  mode: 'update' | 'sync' | undefined,
  lead: { days: number | null; source: LeadSource | 'none' },
): Record<string, string> {
  const out: Record<string, string> = {};
  // NOT the material name: it is the row the user clicked, not something the
  // system worked out. Nothing was estimated about it, so nothing to mark.
  if (prefill?.unit != null) out.unit = prefill.unit;
  if (prefill?.amount != null) out.amount = String(prefill.amount);
  // NOT under sync: there the span is DERIVED from the merged amount, so the
  // incoming `spanDays` describes the older, smaller stock. Seeding it made the
  // note claim "Estimated 5" beside a derived 7 — a suggestion nobody made,
  // presented as one the user had overridden.
  if (mode !== 'sync' && prefill?.spanDays != null) out.spanDays = String(prefill.spanDays);
  // The EFFECTIVE lead, whatever its source — measured, the user's own stored
  // entry, or the site average. The field is offered on every material now, so
  // it opens showing the number the reorder point is actually derived from
  // rather than an empty box beside an alert the user cannot see the basis for.
  //
  // Marked amber only when the figure is NOT the user's own stored entry: a
  // measurement and a site average are both things the system worked out, and
  // a value being shown back to someone who typed it is not a suggestion.
  if (lead.days != null && lead.source !== 'estimated') out.leadDays = String(lead.days);
  if (estimatedRemaining != null) out.remaining = String(estimatedRemaining);
  // NOT waste: its seed follows the remaining field live (see the dialog's
  // `wasteSeed`), so a frozen opening value here would be stale on the first
  // keystroke.
  return out;
}

// The seed rule lives in `shared` — the Waste card judges recorded figures
// against the very same function, so the two cannot drift apart.
export { estimateWaste } from '../../inventory/index.js';

export interface ISyncMerge {
  remainingNum: number;
  remainingValid: boolean;
  hasArrival: boolean;
  arrivedKnown: number | null;
  arrivedNum: number;
  arrivedValid: boolean;
  merged: number | null;
  amountNum: number;
  unitMismatch: boolean;
  derivedSpan: number | null;
  spanValue: string;
}

/**
 * The Sync merge and every figure derived from it: the old stock plus what
 * landed on it, and how long the merged pile should last. Pure — the per-line
 * reasoning lives here with the code it explains. NFKC folds full-width digits
 * (４０ → 40) so IME input parses.
 */
export function computeSyncMerge(input: {
  isSync: boolean;
  remaining: string;
  amount: string;
  arrived: { quantity: number | null; unit: string | null } | null | undefined;
  arrivedQty: string;
  unit: string;
  spanDays: string;
  spanTouched: boolean;
  prefill: IStockPrefill | null | undefined;
}): ISyncMerge {
  const { isSync, remaining, amount, arrived, arrivedQty, unit, spanDays, spanTouched, prefill } = input;

  const remainingNum = parseNumber(remaining);
  // Zero IS valid: "none left, and the new delivery is all of it" is a real
  // answer. EMPTY is not — `Number('')` is 0, so without the blank guard,
  // clearing the field would silently assert the entire batch was consumed.
  const remainingValid =
    remaining.trim() !== '' && Number.isFinite(remainingNum) && remainingNum >= 0;
  // Known from the delivery record, or typed when it recorded none. Nothing
  // landed at all is a row that simply ran out — no arrival to merge.
  const hasArrival = isSync && arrived != null;
  const arrivedKnown = arrived?.quantity ?? null;
  const arrivedRaw = arrivedQty.trim().normalize('NFKC');
  const arrivedNum = arrivedKnown ?? parseNumber(arrivedRaw);
  const arrivedValid =
    !hasArrival ||
    arrivedKnown != null ||
    (arrivedRaw !== '' && Number.isFinite(arrivedNum) && arrivedNum > 0);

  // The delivery's unit and the running stock's must agree before their amounts
  // can be added — compared as UNITS, so 'sheets' and '枚' agree.
  const unitMismatch =
    hasArrival && arrived != null && arrived.unit != null && unit.trim() !== '' &&
    !sameUnit(arrived.unit, unit);

  // No total across units (QA 2026-10-18: 20 kg was saved as 20 bags) — and
  // with no total there is no amount, so the count cannot be saved.
  const merged =
    hasArrival && remainingValid && arrivedValid && !unitMismatch
      ? Math.round((remainingNum + arrivedNum) * 10) / 10
      : null;

  // The total is `left + arrived` by definition — computed, never typed.
  const amountNum = hasArrival ? (merged ?? NaN) : parseNumber(amount);

  // A merged pile lasts in proportion to the rate this material actually runs
  // at — measured cycles beat the entered guess.
  const syncRate =
    hasArrival && prefill?.amount != null
      ? prefill.learnedSpanDays
        ? prefill.amount / prefill.learnedSpanDays
        : prefill.spanDays
          ? prefill.amount / prefill.spanDays
          : null
      : null;
  const derivedSpan =
    merged != null && syncRate ? Math.max(1, Math.min(365, Math.round(merged / syncRate))) : null;
  const spanValue = hasArrival && !spanTouched && derivedSpan != null ? String(derivedSpan) : spanDays;

  return {
    remainingNum, remainingValid, hasArrival, arrivedKnown, arrivedNum,
    arrivedValid, merged, amountNum, unitMismatch, derivedSpan, spanValue,
  };
}

/** The plain values the Save payload derives from — captured as an object so
 *  the builder stays pure and testable without mounting the dialog. */
export interface ISnapshotBodyInputs {
  /** The typed material name, trimmed. */
  query: string;
  mode: 'update' | 'sync' | undefined;
  prefill: IStockPrefill | null | undefined;
  /** The resolved unit label — non-null whenever Save is enabled. */
  unitLabel: string;
  amountNum: number;
  spanNum: number;
  /** The lead field's raw text ('' = cleared). */
  leadDays: string;
  /** The field still holds exactly what the dialog seeded — see `leadSeeded`
   *  in the component for why this compares against the seeded STRING. */
  leadUntouched: boolean;
  leadNum: number;
  remainingNum: number;
  /** The Waste field's parsed value; null when blank. Sync only. */
  wasteNum?: number | null;
  /** The orders this count records as arrived — a Sync's, or an Add stock's
   *  for a delivery that landed untracked (`ISnapshotBody.arrivals`). */
  arrivals?: string[];
}

/**
 * The snapshot write body, under PATCH discipline: an update of an existing
 * material sends ONLY what the user changed. An untouched field must not ride
 * along — a carried-forward `amount` reads to the server as a fresh count and
 * re-anchors the tank's clock, resetting days-left on a lead-only edit.
 *
 * Only a row-tapped edit qualifies as partial (`previous` present, name still
 * the same material by fold, not a sync — a sync IS a full count). A retyped
 * name is a different material: nothing stored to patch, so the full record
 * goes out, exactly as a create does.
 */
export function buildSnapshotBody({
  query,
  mode,
  prefill,
  unitLabel,
  amountNum,
  spanNum,
  leadDays,
  leadUntouched,
  leadNum,
  remainingNum,
  wasteNum,
  arrivals,
}: ISnapshotBodyInputs): ISnapshotBody {
  const isSync = mode === 'sync';
  const prev = prefill?.previous;
  const partial =
    !isSync &&
    prev != null &&
    prefill?.materialName != null &&
    fold(query) === fold(prefill.materialName);
  return {
    materialName: query,
    ...(partial && prev.unit != null && sameUnit(unitLabel, prev.unit)
      ? {}
      : { unit: unitToStore(unitLabel) }),
    ...(partial && amountNum === prev.amount ? {} : { amount: amountNum }),
    ...(partial && spanNum === prev.spanDays ? {} : { spanDays: spanNum }),
    // Sent only when the user CHANGED it. The field arrives prefilled with
    // the effective lead, so an untouched save must not restate it — on a
    // partial update it is simply omitted; elsewhere (create, sync) the
    // untouched value goes out as whatever was stored before, and a
    // seeded-but-untouched estimate stays out of storage so a borrowed site
    // median never freezes over the material's own measured lead.
    //
    // Cleared → null, which hands the lead back to the measurement or the
    // site average.
    ...(partial && leadUntouched
      ? {}
      : {
          estimatedLeadTimeDays:
            leadDays.trim() === ''
              ? null
              : leadUntouched
                ? (prefill?.estimatedLeadTimeDays ?? null)
                : leadNum,
        }),
    ...(mode !== undefined ? { mode } : {}),
    // Only sync consumes it; the server requires it there and ignores it
    // elsewhere, so sending it unconditionally would be a silent lie on a
    // plain update.
    ...(isSync ? { remaining: remainingNum } : {}),
    // Blank means "not recorded": the cycle closes with waste null, not a
    // fabricated zero, so the Waste card only counts what someone confirmed.
    ...(isSync && wasteNum != null ? { waste: wasteNum } : {}),
    // The arrival rides the count's own request, so a refused count can never
    // leave the order marked arrived with nothing reconciled (QA 2026-10-18).
    ...(arrivals != null && arrivals.length > 0 ? { arrivals } : {}),
  };
}

/** The plain values the stock form's Save rule reads — already parsed, so the
 *  rule stays pure and both clients judge a count the same way. */
export interface IStockSaveInputs {
  isSync: boolean;
  /** The typed material name, trimmed. */
  query: string;
  /** The unit resolves to a real option — a unit is never free text. */
  unitResolved: boolean;
  /** From `computeSyncMerge`; only read under sync. */
  remainingValid: boolean;
  arrivedValid: boolean;
  /** The Waste field's parsed value; null when blank. Only read under sync. */
  wasteNum: number | null;
  /** What was left of the old stock — waste is counted out of it
   *  (`wasteExceedsLeft`). Optional: a client that omits it leaves the check
   *  to the server. */
  remainingNum?: number;
  amountNum: number;
  spanNum: number;
}

/** A loss cannot exceed the leftover it is counted out of — the server's
 *  `waste-exceeds-remaining`, judged before Save instead of after it. */
export function wasteExceedsLeft(wasteNum: number | null, remainingNum: number | undefined): boolean {
  return wasteNum != null && remainingNum != null && Number.isFinite(remainingNum) && wasteNum > remainingNum;
}

/**
 * Whether the stock form's figures make a count the server will take. The
 * lead field is judged on its own (`leadEntryValid`), since whether a blank
 * lead is acceptable depends on what can stand in for it.
 */
export function stockSaveValid({
  isSync,
  query,
  unitResolved,
  remainingValid,
  arrivedValid,
  wasteNum,
  remainingNum,
  amountNum,
  spanNum,
}: IStockSaveInputs): boolean {
  // Blank waste is allowed (nothing recorded); a typed value must be a real
  // quantity, and no more than what was left.
  const wasteValid =
    wasteNum == null || (Number.isFinite(wasteNum) && wasteNum >= 0 && !wasteExceedsLeft(wasteNum, remainingNum));
  return (
    query.length > 0 &&
    unitResolved &&
    (!isSync || (remainingValid && arrivedValid && wasteValid)) &&
    stockAmountIssue(amountNum, isSync) === null &&
    Number.isInteger(spanNum) &&
    spanNum >= 1 &&
    spanNum <= 365 // backend cap — reject here instead of a 400 round-trip
  );
}

/**
 * Why the stock amount is refused, or null. A sync may honestly land on zero —
 * "nothing arrived, nothing left" is a real count, and it used to be the one
 * value the form refused, leaving the ran-out prompt permanent. A plain add
 * still needs a positive amount: there is no cycle to close there, so a zero
 * records a material that does not exist. Either way the server caps it at
 * `MAX_QUANTITY`.
 */
export function stockAmountIssue(amountNum: number, isSync: boolean): QuantityIssue | null {
  return amountIssue(amountNum, isSync);
}

/**
 * A typed lead is a whole number of days, 0–365. 0 is valid: a same-day
 * delivery measures a 0-day lead, and the field is prefilled with it. The
 * non-empty guard is load-bearing: `Number('')` is 0, so without it an EMPTY
 * field (no lead known) would read as a deliberate 0-day lead.
 */
export function leadEntryValid(leadDays: string): boolean {
  const leadNum = parseNumber(leadDays);
  return leadDays.trim() !== '' && Number.isInteger(leadNum) && leadNum >= 0 && leadNum <= 365;
}
